/*
 * Cart drawer (sections/cart-drawer.liquid).
 *
 * Opens after any add to cart, wherever it came from: the product form (theme.js),
 * collection grid forms (ajaxify-cart), or bundle apps. Rather than hooking each of
 * those, it watches the browser's resource timing entries for /cart/add, /cart/change,
 * /cart/update and /cart/clear requests. Wrapping fetch/XHR does not work here: apps on
 * the site replace XMLHttpRequest.prototype.open with a copy that skips earlier wrappers.
 * Resource timing does not reliably give the status code (Safari), so the drawer only
 * opens after an add if the cart actually changed. The drawer's own requests, and the
 * gift-with-purchase requests (gift-tiers.js), carry ?ss_internal=1 so they are ignored.
 *
 * All markup comes from Liquid: after a change the drawer re-renders #CartDrawerBody
 * from the Section Rendering API. Elements are always looked up fresh, because the
 * theme editor can replace the whole section.
 */
(function () {
  'use strict';

  if (!document.getElementById('CartDrawer') || window.SSCartDrawer) return;

  var SECTION = 'cart-drawer';
  var RECS_SECTION = 'cart-drawer-recommendations';
  var RECS_SHOWN = 4;
  var CART_REQUEST = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var OWN_REQUEST = 'ss_internal=1';
  var isOpen = false;
  var lastFocus = null;
  var recsCache = {};

  function drawer() { return document.getElementById('CartDrawer'); }
  function panel() { var d = drawer(); return d && d.querySelector('.cart-drawer__panel'); }
  function body() { return document.getElementById('CartDrawerBody'); }
  function rootUrl() {
    var d = drawer();
    var root = (d && d.getAttribute('data-root-url')) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function announce(text) {
    var live = drawer() && drawer().querySelector('[data-cart-drawer-live]');
    if (live) live.textContent = text;
  }

  /* ---------- Watching cart requests made by anything else on the page ---------- */

  function request(url, options) {
    return window.fetch(url, options);
  }

  function requestKind(url) {
    var match = String(url || '').match(CART_REQUEST);
    return match ? match[1] : null;
  }

  var pendingTimer = null;
  var openAfterRefresh = false;

  function onCartRequest(kind) {
    if (kind === 'add') openAfterRefresh = true;
    clearTimeout(pendingTimer);
    // A bundle add can fire several requests in a row; refresh once after they settle.
    pendingTimer = setTimeout(function () {
      var shouldOpen = openAfterRefresh;
      var before = body() && body().getAttribute('data-signature');
      openAfterRefresh = false;
      syncGifts()
        .then(refresh)
        .then(function () {
          // A failed add (e.g. out of stock) leaves the cart unchanged: stay closed.
          if (shouldOpen && body().getAttribute('data-signature') !== before) open();
        })
        .catch(function () { if (shouldOpen) window.location.href = rootUrl() + 'cart'; });
    }, 80);
  }

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
          if (entry.name.indexOf(OWN_REQUEST) !== -1) return;
          if (entry.responseStatus && (entry.responseStatus < 200 || entry.responseStatus >= 300)) return;
          var kind = requestKind(entry.name);
          if (kind) onCartRequest(kind);
        });
      }).observe({ type: 'resource' });
    } catch (e) {}
  }

  /* ---------- Rendering ---------- */

  function updateCount(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count]'), function (el) {
      el.textContent = count;
    });
    Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count-bubble]'), function (el) {
      el.classList.toggle('hide', count === 0);
    });
  }

  function render(sectionHtml) {
    var doc = new DOMParser().parseFromString(sectionHtml || '', 'text/html');
    var next = doc.getElementById('CartDrawerBody');
    var current = body();
    if (!next || !current) throw new Error('Cart drawer markup missing');

    // Keep the scroll position and the focused control across the swap.
    var scroller = current.querySelector('[data-cart-drawer-scroll]');
    var scrollTop = scroller ? scroller.scrollTop : 0;
    var active = document.activeElement;
    var focusLine = null;
    var focusAction = null;
    if (active && current.contains(active)) {
      var line = active.closest('[data-line-key]');
      focusLine = line && line.getAttribute('data-line-key');
      focusAction = active.getAttribute('data-action');
    }

    current.parentNode.replaceChild(document.importNode(next, true), current);

    var fresh = body();
    var newScroller = fresh.querySelector('[data-cart-drawer-scroll]');
    if (newScroller) newScroller.scrollTop = scrollTop;
    updateCount(parseInt(fresh.getAttribute('data-item-count'), 10) || 0);

    if (active && !document.body.contains(active) && isOpen) {
      var target = null;
      if (focusLine) {
        var lines = fresh.querySelectorAll('[data-line-key]');
        for (var i = 0; i < lines.length; i++) {
          if (lines[i].getAttribute('data-line-key') === focusLine) {
            target = lines[i].querySelector('[data-action="' + focusAction + '"]');
            break;
          }
        }
      }
      (target || panel()).focus();
    }

    if (isOpen) loadRecs();
  }

  function refresh() {
    return request(rootUrl() + '?sections=' + SECTION, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart drawer refresh failed');
        return response.json();
      })
      .then(function (sections) { render(sections[SECTION]); });
  }

  /* ---------- Changing lines and adding recommendations ---------- */

  function postCart(path, payload) {
    payload.sections = SECTION;
    payload.sections_url = rootUrl();
    return request(rootUrl() + path + '?' + OWN_REQUEST, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || 'Cart error');
          error.cartMessage = data.description || data.message;
          throw error;
        }
        return data;
      });
    });
  }

  // Adds or removes gift-with-purchase lines (assets/gift-tiers.js). Resolves to true
  // if it changed the cart.
  function syncGifts() {
    return window.SSGifts ? window.SSGifts.sync() : Promise.resolve(false);
  }

  function applyResult(data) {
    var rendered = data.sections && data.sections[SECTION]
      ? Promise.resolve(render(data.sections[SECTION]))
      : refresh();
    return rendered
      .then(syncGifts)
      .then(function (changed) { if (changed) return refresh(); });
  }

  // aria-disabled rather than disabled: a disabled button drops keyboard focus.
  function setLineBusy(line, busy) {
    line.classList.toggle('is-busy', busy);
    Array.prototype.forEach.call(line.querySelectorAll('button'), function (button) {
      if (busy) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  }

  function changeLine(button) {
    var line = button.closest('[data-line-key]');
    var quantity = parseInt(button.getAttribute('data-line-qty'), 10);
    var error = line.querySelector('[data-line-error]');
    if (error) error.textContent = '';
    setLineBusy(line, true);

    postCart('cart/change.js', { id: line.getAttribute('data-line-key'), quantity: quantity })
      .then(function (data) {
        announce(quantity === 0 ? 'Item removed. Cart updated.' : 'Cart updated.');
        return applyResult(data);
      })
      .catch(function (err) {
        if (!document.body.contains(line)) return;
        setLineBusy(line, false);
        if (error) error.textContent = err.cartMessage || 'Something went wrong. Please try again.';
      });
  }

  function addVariant(button) {
    var label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    // Pack products (snippets/pack-rules.liquid) add enough to finish the pack.
    var quantity = parseInt(button.getAttribute('data-add-qty'), 10) || 1;
    postCart('cart/add.js', { items: [{ id: parseInt(button.getAttribute('data-add-variant'), 10), quantity: quantity }] })
      .then(function (data) {
        announce('Added to cart.');
        return applyResult(data);
      })
      .catch(function (err) {
        if (!document.body.contains(button)) return;
        button.textContent = err.cartMessage ? 'Unavailable' : label;
        button.disabled = !!err.cartMessage;
      });
  }

  /* ---------- Recommendations ---------- */

  function loadRecs() {
    var current = body();
    var box = current && current.querySelector('[data-cart-drawer-recs]');
    var productId = current && current.getAttribute('data-newest-product');
    // Gap products are rendered by Liquid with the drawer; related products are only the fallback.
    if (!box || !productId || box.hasAttribute('data-gap-fill')) return;

    var inCart = (current.getAttribute('data-cart-products') || '').split(',');

    var cached = recsCache[productId];
    var pending = cached || request(
      drawer().getAttribute('data-recs-url') + '?section_id=' + RECS_SECTION + '&product_id=' + productId + '&limit=10&intent=related',
      { credentials: 'same-origin' }
    ).then(function (response) {
      if (!response.ok) throw new Error('Recommendations failed');
      return response.text();
    });
    recsCache[productId] = pending;
    pending.catch(function () { delete recsCache[productId]; });

    pending.then(function (html) {
      // The drawer may have re-rendered while this was loading.
      var latest = body();
      if (!latest || latest.getAttribute('data-newest-product') !== productId) return;
      var target = latest.querySelector('[data-cart-drawer-recs]');
      var list = target && target.querySelector('[data-cart-drawer-recs-list]');
      if (!list) return;

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var cards = Array.prototype.filter.call(doc.querySelectorAll('[data-product-id]'), function (card) {
        return inCart.indexOf(card.getAttribute('data-product-id')) === -1;
      }).slice(0, RECS_SHOWN);

      list.innerHTML = '';
      cards.forEach(function (card) { list.appendChild(document.importNode(card, true)); });
      target.hidden = cards.length === 0;
    }).catch(function () {});
  }

  /* ---------- Open and close ---------- */

  function focusable() {
    var p = panel();
    if (!p) return [];
    return Array.prototype.filter.call(
      p.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0; }
    );
  }

  function onKeydown(event) {
    if (event.key === 'Escape') { close(); return; }
    if (event.key !== 'Tab') return;
    var items = focusable();
    if (!items.length) { event.preventDefault(); return; }
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel())) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function open() {
    var d = drawer();
    if (!d || isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    document.documentElement.classList.add('cart-drawer-lock');
    document.addEventListener('keydown', onKeydown);
    setTimeout(function () { var p = panel(); if (p) p.focus(); }, 30);
    loadRecs();
  }

  function close() {
    var d = drawer();
    if (!d || !isOpen) return;
    isOpen = false;
    d.classList.remove('is-open');
    document.documentElement.classList.remove('cart-drawer-lock');
    document.removeEventListener('keydown', onKeydown);
    if (lastFocus && document.body.contains(lastFocus)) lastFocus.focus();
  }

  /* ---------- Clicks ---------- */

  document.addEventListener('click', function (event) {
    var target = event.target;

    var cartLink = target.closest('a.site-header__cart');
    if (cartLink) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      open();
      refresh().catch(function () {});
      return;
    }

    var d = drawer();
    if (!d || !d.contains(target)) return;

    if (target.closest('[data-cart-drawer-close]')) { close(); return; }

    var lineButton = target.closest('[data-line-qty]');
    if (lineButton) {
      if (lineButton.getAttribute('aria-disabled') !== 'true') changeLine(lineButton);
      return;
    }

    var addButton = target.closest('[data-add-variant]');
    if (addButton && !addButton.disabled) { addVariant(addButton); }
  });

  // Coming back via the browser's back button can show a stale cached page.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh().catch(function () {});
  });

  // Theme editor: show the drawer while its section is selected.
  document.addEventListener('shopify:section:select', function (event) {
    if (event.detail && event.detail.sectionId === SECTION) { isOpen = false; open(); }
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    if (event.detail && event.detail.sectionId === SECTION) close();
  });

  window.SSCartDrawer = { open: open, close: close, refresh: refresh };

  // Correct the gifts once per page load (e.g. the tiers changed with a new promo theme).
  // gift-tiers.js is also deferred, so wait until both scripts have run.
  document.addEventListener('DOMContentLoaded', function () {
    var current = body();
    if (!current || current.getAttribute('data-item-count') === '0') return;
    syncGifts().then(function (changed) { if (changed) return refresh(); }).catch(function () {});
  });
})();
